import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import { RouteComponentProps } from 'react-router-dom';
import ZoomContext from '../../context/zoom-context';
import ZoomMediaContext from '../../context/media-context';
import AvatarActionContext from './context/avatar-context';
import Avatar from './components/avatar';
import VideoFooter from './components/video-footer';
import Pagination from './components/pagination';
import { useCanvasDimension } from './hooks/useCanvasDimension';
import { useGalleryLayout } from './hooks/useGalleryLayout';
import { usePagination } from './hooks/usePagination';
import { useActiveVideo } from './hooks/useAvtiveVideo';
import { useAvatarAction } from './hooks/useAvatarAction';
import { useNetworkQuality } from './hooks/useNetworkQuality';
import ShareView from './components/share-view';

import './video.scss';
import { Participant, VideoPlayer, VideoQuality } from '@zoom/videosdk';
import { useParticipantsChange } from './hooks/useParticipantsChange';
import { useSpotlightVideo } from './hooks/useSpotlightVideo';
import { UserRole, ViewContext } from '../../App';
import { Radio } from 'antd';
import { useVideoAspect } from './hooks/useVideoAspectRatio';
import Header from '../../component/header/header';

// ─── Constants ────────────────────────────────────────────────────────────────
const MIN_CONTAINER_H = 300;
const targetSideRatio = 4 / 3;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function calcCardSize(
  containerW: number,
  containerH: number,
  cols: number,
  rows: number,
  gap: number,
  targetRatio: number
): { cardW: number; cardH: number } {
  const naturalW = (containerW - gap * (cols - 1)) / cols;
  const naturalH = (containerH - gap * (rows - 1)) / rows;
  const ratio = naturalW / naturalH;
  let cardW = 0;
  let cardH = 0;
  if (ratio > targetRatio) {
    // Container is wider than target ratio – limit by width
    cardH = naturalH;
    cardW = cardH * targetRatio;
  } else {
    // Container is taller than target ratio – limit by height
    cardW = naturalW;
    cardH = cardW / targetRatio;
  }
  return { cardW, cardH };
}

function getLayout(n: number): [number, number] {
  const map: Record<number, [number, number]> = {
    1: [1, 1],
    2: [2, 1],
    3: [2, 2],
    4: [2, 2],
    5: [3, 2],
    6: [3, 2],
    7: [4, 2],
    8: [4, 2]
  };
  return map[n] ?? [4, 2];
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface VideoRouteState {
  isStartedAudio?: boolean;
  isStartedVideo?: boolean;
  isMuted?: boolean;
  hasMicrophonePermission?: boolean | null;
  hasCameraPermission?: boolean | null;
  activeMicrophone?: string;
  activeCamera?: string;
  activeSpeaker?: string;
}

interface VideoProps extends RouteComponentProps<Record<string, string>, any, unknown> {
  userName: string;
}

interface ParticipantCardProps {
  user: Participant;
  avatarSize: number;
  style?: React.CSSProperties;
  // These are passed down from VideoContainer to avoid closure issues
  avatarActionState: ReturnType<typeof useAvatarAction>;
  activeVideo: number;
  networkQuality: Record<string, any>;
  aspectRatio: Record<string, number>;
  optionsOfVideoResolution: { label: string; value: VideoQuality }[];
  onVideoResolutionChange: (e: any, userId: number) => void;
  setVideoPlayerRef: (userId: number, element: VideoPlayer | null) => void;
}

interface GridLayoutProps {
  participants: Participant[];
  cardProps: Omit<ParticipantCardProps, 'user' | 'avatarSize' | 'style'>;
  shareViewNode?: React.ReactNode;
  isSharing?: boolean;
}

interface SidebarLayoutProps {
  isSharing: boolean;
  mainParticipant: Participant | null | undefined;
  sideParticipants: Participant[];
  containerHeight: number;
  cardProps: Omit<ParticipantCardProps, 'user' | 'avatarSize' | 'style'>;
  shareViewNode?: React.ReactNode;
  targetRatio: number;
}

// ─── Sub-components (defined OUTSIDE VideoContainer to avoid re-creation) ─────
function ParticipantCard({
  user,
  avatarSize,
  style = {},
  avatarActionState,
  activeVideo,
  networkQuality,
  aspectRatio,
  optionsOfVideoResolution,
  onVideoResolutionChange,
  setVideoPlayerRef
}: ParticipantCardProps) {
  const cardStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '1 1 auto',
    minWidth: 0,
    minHeight: 0,
    boxSizing: 'border-box',
    ...style
  };

  return (
    <div style={cardStyle}>
      <video-player-container className="unified-self-view-container gallery-view">
        <AvatarActionContext.Provider value={avatarActionState}>
          <div
            className={classnames('video-cell', { 'video-cell-spotlight': (user as any).spotlighted })}
            key={user.userId}
          >
            {/* {avatarActionState?.avatarActionState[user?.userId]?.videoResolutionAdjust?.toggled && (
              <div className="change-video-resolution">
                <Radio.Group
                  options={optionsOfVideoResolution}
                  onChange={(value) => onVideoResolutionChange(value, user.userId)}
                  defaultValue={VideoQuality.Video_720P}
                  optionType="button"
                  buttonStyle="solid"
                />
              </div>
            )} */}
            <div /*className="aspact-ratio" */ style={{ height: '100%', width: '100%' }}>
              {user.bVideoOn && (
                <video-player
                  key={user.userId + 'video-player'}
                  class="video-player"
                  ref={(element: VideoPlayer | null) => setVideoPlayerRef(user.userId, element)}
                />
              )}
              <Avatar
                participant={user}
                key={user.userId}
                isActive={activeVideo === user.userId}
                networkQuality={networkQuality[`${user.userId}`]}
              />
            </div>
          </div>
        </AvatarActionContext.Provider>
      </video-player-container>
    </div>
  );
}

function GridLayout({ participants, cardProps, shareViewNode, isSharing }: GridLayoutProps) {
  const targetRatio = 16 / 9;
  const sharingNumber = shareViewNode && isSharing ? 1 : 0;
  const n = participants.length + sharingNumber;
  const gap = 10;

  // Measure container size via ResizeObserver
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      // Only update state when size actually changes to avoid infinite loops
      setSize((prev) => {
        if (prev.w === width && prev.h === height) return prev;
        return { w: width, h: height };
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []); // empty deps – the ref is stable

  const { w, h } = size;

  const getAvatarSize = (cardW: number, cardH: number): number => Math.min(cardW, cardH) * 0.38;

  // ── general case ──────────────────────────────────────────────────────────
  const [cols, rows] = getLayout(n);
  const { cardW, cardH } = w > 0 && h > 0 ? calcCardSize(w, h, cols, rows, gap, targetRatio) : { cardW: 0, cardH: 0 };
  const totalGridW = cardW * cols + gap * (cols - 1);
  const totalGridH = cardH * rows + gap * (rows - 1);
  const avatarSize = getAvatarSize(cardW, cardH);
  // console.log('Container: ', w, 'x', h);
  // console.log('Card: ', cardW, 'x', cardH);
  return (
    <div
      ref={wrapRef}
      style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {w > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, ${cardW}px)`,
            gridTemplateRows: `repeat(${rows}, ${cardH}px)`,
            gap,
            width: totalGridW,
            height: totalGridH
          }}
        >
          {shareViewNode && (
            <div
              style={{
                position: isSharing ? 'relative' : 'absolute',
                width: isSharing ? '100%' : 0,
                height: isSharing ? '100%' : 0,
                overflow: 'hidden',
                borderRadius: 8
              }}
            >
              {shareViewNode}
            </div>
          )}
          {participants.map((p, index) => {
            const isOddNumber = n % 2 === 1;
            const realIndex = index + sharingNumber;
            const startRow2 = (n + (n % 2)) / 2;
            const showMargin = isOddNumber && realIndex >= startRow2;
            // console.log('Log: ', index, ' - ', isOddNumber, ' - ', realIndex, ' - ', startRow2, ' - ', showMargin);
            return (
              <div
                key={`grid-card-${index}`}
                style={{
                  height: cardH,
                  width: cardW
                }}
              >
                <ParticipantCard
                  key={p.userId}
                  user={p}
                  avatarSize={avatarSize}
                  style={{
                    width: '100%',
                    height: '100%',
                    marginLeft: showMargin ? cardW / 2 : 0
                  }}
                  {...cardProps}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SidebarLayout({
  isSharing,
  mainParticipant,
  sideParticipants,
  containerHeight,
  cardProps,
  shareViewNode,
  targetRatio
}: SidebarLayoutProps) {
  // Measure container size via ResizeObserver
  const sideRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  // console.log('Target Ratio: ', targetRatio);
  useEffect(() => {
    const el = sideRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;

      // Only update state when size actually changes to avoid infinite loops
      setSize((prev) => {
        if (prev.w === width && prev.h === height) return prev;
        return { w: width, h: height };
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []); // empty deps – the ref is stable

  const n = sideParticipants.length + (shareViewNode && isSharing ? 1 : 0);
  const gap = 8;
  const sideCols = n >= 4 ? 2 : 1;
  const sideRows = Math.ceil(n / sideCols);
  const sideWidth = sideCols === 2 ? 410 : 200;
  const centerContainerWidth = size.w - sideWidth - gap;
  const centerContainerHeight = size.h;

  const naturalCardW = (sideWidth - gap * (sideCols - 1)) / sideCols;
  const naturalCardH = (containerHeight - gap * (sideRows - 1)) / sideRows;
  const ratio = naturalCardW / naturalCardH;
  let cardH = Math.min(naturalCardH, naturalCardW);
  let cardW = cardH * targetSideRatio;
  if (ratio > targetSideRatio) {
    cardH = naturalCardH;
    cardW = cardH * targetSideRatio;
  } else {
    cardW = naturalCardW;
    cardH = cardW / targetSideRatio;
  }
  const centerRatio = centerContainerWidth / centerContainerHeight;

  const avatarSize = Math.min(cardW, cardH) * 0.35;
  const panelH = cardH * sideRows + gap * (sideRows - 1);

  let centerWidth = 0;
  let centerHeight = 0;
  if (centerRatio > targetRatio) {
    // container is wider than 4:3 – limit by height
    centerHeight = size.h;
    centerWidth = centerHeight * targetRatio;
  } else {
    centerWidth = size.w - sideWidth - gap;
    centerHeight = centerWidth / targetRatio;
  }

  // Guard: Sidebar not render without a valid mainParticipant
  if (!mainParticipant) return null;

  return (
    <div ref={sideRef} style={{ display: 'flex', gap: 10, width: '100%', height: '100%' }}>
      {/* Main screen */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <div
          style={{
            width: centerWidth,
            height: centerHeight
          }}
        >
          <ParticipantCard
            key={mainParticipant.userId}
            user={mainParticipant}
            avatarSize={avatarSize}
            style={{
              width: centerWidth,
              height: centerHeight
            }}
            {...cardProps}
          />
        </div>
      </div>

      {/* Side panel – vertically centered, landscape cards, no scroll */}
      <div
        style={{
          width: sideWidth,
          height: '100%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${sideCols}, ${cardW}px)`,
            gridTemplateRows: `repeat(${sideRows}, ${cardH}px)`,
            gap,
            width: sideWidth,
            height: panelH
          }}
        >
          {shareViewNode && (
            <div
              style={{
                position: isSharing ? 'relative' : 'absolute',
                width: isSharing ? '100%' : 0,
                height: isSharing ? '100%' : 0,
                overflow: 'hidden',
                borderRadius: 8
              }}
            >
              {shareViewNode}
            </div>
          )}
          {sideParticipants.map((p) => (
            <ParticipantCard
              key={p.userId}
              user={p}
              avatarSize={avatarSize}
              style={{ width: '100%', height: '100%' }}
              {...cardProps}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface SidePanelProps {
  sideParticipants: Participant[];
  containerHeight: number;
  cardProps: Omit<ParticipantCardProps, 'user' | 'avatarSize' | 'style'>;
}

function SidePanel({ sideParticipants, containerHeight, cardProps }: SidePanelProps) {
  const gap = 8;
  const n = sideParticipants.length;
  const sideCols = n >= 4 ? 2 : 1;
  const sideRows = Math.ceil(Math.max(n, 1) / sideCols);
  const sideWidth = sideCols === 2 ? 410 : 200;

  const naturalCardW = (sideWidth - gap * (sideCols - 1)) / sideCols;
  const naturalCardH = (containerHeight - gap * (sideRows - 1)) / sideRows;
  const ratio = naturalCardW / naturalCardH;
  let cardH = Math.min(naturalCardH, naturalCardW);
  let cardW = cardH * targetSideRatio;
  if (ratio > targetSideRatio) {
    cardH = naturalCardH;
    cardW = cardH * targetSideRatio;
  } else {
    cardW = naturalCardW;
    cardH = cardW / targetSideRatio;
  }

  const avatarSize = Math.min(cardW, cardH) * 0.35;
  const panelH = cardH * sideRows + gap * (sideRows - 1);

  return (
    <div
      style={{
        width: sideWidth,
        height: '100%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${sideCols}, ${cardW}px)`,
          gridTemplateRows: `repeat(${sideRows}, ${cardH}px)`,
          gap,
          width: sideWidth,
          height: panelH
        }}
      >
        {sideParticipants.map((p) => (
          <ParticipantCard
            key={p.userId}
            user={p}
            avatarSize={avatarSize}
            style={{ width: '100%', height: '100%' }}
            {...cardProps}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main VideoContainer ───────────────────────────────────────────────────────

const VideoContainer: React.FunctionComponent<VideoProps> = (props) => {
  const zmClient = useContext(ZoomContext);
  const {
    mediaStream,
    video: { decode: isVideoDecodeReady }
  } = useContext(ZoomMediaContext);
  const params = props.match.params as { slotBookingId: string; userId: string };
  const routeState = props.location.state as VideoRouteState | undefined;
  const { userName } = props;
  const slotBookingId = params.slotBookingId;
  const userId = params.userId;
  const videoRef = useRef<VideoPlayer | null>(null);
  const videoCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const shareViewRef = useRef<{ selfShareRef: HTMLCanvasElement | HTMLVideoElement | null }>(null);
  const [isReceiveSharing, setIsReceiveSharing] = useState(false);
  const canvasDimension = useCanvasDimension(mediaStream, videoCanvasRef);
  const videoPlayerListRef = useRef<Record<string, VideoPlayer>>({});
  const playerResizeObservers = useRef<Record<string, ResizeObserver>>({});
  const videoPlayerInnerRef = useRef<Record<string, HTMLElement>>({});
  const videoPlayerVideoRef = useRef<Record<string, HTMLVideoElement>>({});
  const mainViewRef = useRef<VideoPlayer>(null);
  const activeVideo = useActiveVideo(zmClient);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const viewContext = useContext(ViewContext);
  const aspectRatio = useVideoAspect(zmClient);

  // Track video area height via ResizeObserver
  const videoAreaRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(MIN_CONTAINER_H);
  const [currentLayout, setCurrentLayout] = useState<'grid' | 'sidebar' | 'share'>('grid');
  const [requestUpdate, setRequestUpdate] = useState<boolean>(false);
  const [mobileOrientations, setMobileOrientations] = useState<Map<string, boolean>>(new Map());
  const [targetRatio, setTargetRatio] = useState<number>(16 / 9);

  useEffect(() => {
    // console.log('Mobile orientations changed: ', mobileOrientations);

    const mainUserId = viewContext.mainViewUser?.userId;
    // console.log('Main user: ', viewContext.mainViewUser);
    if (mainUserId === undefined) {
      setTargetRatio(16 / 9);
      return;
    }
    const orientation = mobileOrientations.get(String(mainUserId));
    setTargetRatio(orientation !== undefined ? (orientation ? 16 / 9 : 9 / 16) : 16 / 9);
  }, [viewContext.mainViewUser, mobileOrientations]);

  const nextLayout: 'grid' | 'sidebar' | 'share' =
    isReceiveSharing && viewContext.pinViewUser == null && !viewContext.galleryView
      ? 'share'
      : viewContext.speakerView && viewContext.mainViewUser
        ? 'sidebar'
        : 'grid';

  // When layout changes → update state
  useEffect(() => {
    setCurrentLayout(nextLayout);
  }, [nextLayout]);

  useEffect(() => {
    const el = videoAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const h = Math.max(entries[0].contentRect.height - 32, MIN_CONTAINER_H);
      setContainerHeight((prev) => (prev === h ? prev : h)); // avoid unnecessary re-renders
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const optionsOfVideoResolution = [
    { label: '1080P', value: VideoQuality.Video_1080P },
    { label: '720P', value: VideoQuality.Video_720P },
    { label: '360P', value: VideoQuality.Video_360P },
    { label: '180P', value: VideoQuality.Video_180P },
    { label: '90P', value: VideoQuality.Video_90P }
  ];

  useSpotlightVideo(zmClient, mediaStream, (p) => {
    setSpotlightUsers(p.map((user) => user.userId));
    if (p.length > 0) viewContext.setSpeakerView(true);
  });

  const [spotlightUsers, setSpotlightUsers] = useState<number[]>([]);

  const { page, pageSize, totalPage, totalSize, setPage } = usePagination(
    zmClient,
    canvasDimension,
    viewContext.speakerView
  );
  const { visibleParticipants, layout: videoLayout } = useGalleryLayout(
    zmClient,
    mediaStream,
    isVideoDecodeReady,
    videoRef,
    canvasDimension,
    { page, pageSize, totalPage, totalSize }
  );

  useParticipantsChange(zmClient, () => {
    setParticipants(zmClient.getAllUser());
  });

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('User quay lại tab');
        setRequestUpdate((prev) => !prev); // trigger updates in child components

        visibleParticipants.forEach((user) => {
          if (user.bVideoOn) {
            const el = videoPlayerListRef.current[`${user.userId}`];
            if (el) {
              mediaStream?.attachVideo(user.userId, VideoQuality.Video_720P, el);
            }
          } else {
            mediaStream?.detachVideo(user.userId);
          }
        });

        const mainUser = viewContext.mainViewUser as Participant | null | undefined;
        if (mainUser) {
          if (mainUser.bVideoOn) {
            const el = videoPlayerListRef.current[`${mainUser.userId}`];
            if (el) mediaStream?.attachVideo(mainUser.userId, VideoQuality.Video_720P, el);
          } else {
            mediaStream?.detachVideo(mainUser.userId);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [visibleParticipants, viewContext.mainViewUser, mediaStream]);

  const currentUserIndex = participants.findIndex((user) => user.userId === zmClient.getCurrentUserInfo()?.userId);
  const currentRole = participants[currentUserIndex]?.userIdentity?.slice(0, 1);

  const avatarActionState = useAvatarAction(zmClient, visibleParticipants);
  const networkQuality = useNetworkQuality(zmClient);

  const setVideoPlayerRef = useCallback(
    (userId: number, element: VideoPlayer | null) => {
      const id = `${userId}`;
      // Cleanup when element is unmounted
      if (!element) {
        if (videoPlayerListRef.current[id]) delete videoPlayerListRef.current[id];
        if (videoPlayerInnerRef.current[id]) delete videoPlayerInnerRef.current[id];
        if (videoPlayerVideoRef.current[id]) delete videoPlayerVideoRef.current[id];
        const existing = playerResizeObservers.current[id];
        if (existing) {
          try {
            existing.disconnect();
          } catch (e) {}
          delete playerResizeObservers.current[id];
        }
        return;
      }

      // Store reference
      videoPlayerListRef.current[id] = element;

      // The VideoPlayer is a custom element; cast to HTMLElement to read layout properties
      const node = element as unknown as HTMLElement;

      // Prefer the actual <video> element inside the player, fall back to inner divs
      const videoEl = node.querySelector('video') as HTMLVideoElement | null;
      const inner =
        (node.querySelector('div > div') as HTMLElement | null) || (node.querySelector('div') as HTMLElement | null);
      const targetEl = videoEl ?? inner ?? node;

      // Save reference to the inner/displayed element for external queries
      videoPlayerInnerRef.current[id] = targetEl as HTMLElement;
      if (videoEl) {
        videoPlayerVideoRef.current[id] = videoEl;
      }

      // Log initial size of the displayed element
      try {
        const rect = targetEl.getBoundingClientRect();
        // console.log('video-player displayed initial size', userId, rect.width, rect.height);
      } catch (e) {
        // console.log('video-player displayed initial size: unable to read bounding rect', userId);
      }

      // (Re)attach a ResizeObserver to log future size changes on the displayed element
      if (playerResizeObservers.current[id]) {
        try {
          playerResizeObservers.current[id].disconnect();
        } catch (e) {}
      }

      try {
        const ro = new ResizeObserver((entries) => {
          const r = entries[0].contentRect;
          // console.log('video-player displayed resized', userId, r.width, r.height);
        });
        ro.observe(targetEl as Element);
        playerResizeObservers.current[id] = ro;
      } catch (e) {
        // ResizeObserver may not be available in some environments; ignore silently
      }

      // If this is the main participant in speaker view, attach immediately when DOM mounts
      const mainUser = viewContext.mainViewUser as Participant | null | undefined;
      if (mainUser && mainUser.userId === userId && mainUser.bVideoOn) {
        mediaStream?.attachVideo(userId, VideoQuality.Video_720P, element);
      }
    },
    [viewContext.mainViewUser, mediaStream]
  );

  // Cleanup all observers on unmount
  useEffect(() => {
    return () => {
      Object.values(playerResizeObservers.current).forEach((ro) => {
        try {
          ro.disconnect();
        } catch (e) {}
      });
      playerResizeObservers.current = {};
    };
  }, []);

  useEffect(() => {
    if (isReceiveSharing && viewContext.pinViewUser == null && !viewContext.galleryView) {
      viewContext.setSpeakerView(true);
      viewContext.setGalleryView(false);
      viewContext.setPinViewUser(null);
      viewContext.setMainViewUser(null);
    } else {
      if (viewContext.pinViewUser == null) {
        viewContext.setMainViewUser(null);
        if (currentRole === UserRole.Student) {
          const instructor = participants.find((user) => user.userIdentity?.slice(0, 1) === UserRole.Instructor);
          if (instructor) viewContext.setMainViewUser(instructor);
        } else {
          const student = participants.find((user) => user.userIdentity?.slice(0, 1) === UserRole.Student);
          if (student) viewContext.setMainViewUser(student);
        }
      }
    }
  }, [participants, isReceiveSharing]);

  useEffect(() => {
    if (viewContext.mainViewUser) {
      const updated = participants.find((u) => u.userId === viewContext.mainViewUser?.userId);
      viewContext.setMainViewUser(updated);
    }
  }, [participants]);

  const onVideoResolutionChange = useCallback(
    ({ target: { value } }: any, userId: number) => {
      const attachment = videoPlayerListRef.current[`${userId}`];
      mediaStream?.attachVideo(userId, value, attachment);
    },
    [videoPlayerListRef, mediaStream]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      visibleParticipants.forEach((user) => {
        if (user.bVideoOn) {
          const el = videoPlayerListRef.current[`${user.userId}`];
          if (el) {
            mediaStream?.attachVideo(user.userId, VideoQuality.Video_720P, el);
          }
        } else {
          mediaStream?.detachVideo(user.userId);
        }
      });

      const mainUser = viewContext.mainViewUser as Participant | null | undefined;
      if (mainUser) {
        if (mainUser.bVideoOn) {
          const el = videoPlayerListRef.current[`${mainUser.userId}`];
          if (el) mediaStream?.attachVideo(mainUser.userId, VideoQuality.Video_720P, el);
        } else {
          mediaStream?.detachVideo(mainUser.userId);
        }
      }
    }, 100); // 100ms is enough for DOM to render

    return () => clearTimeout(timer);
  }, [visibleParticipants, viewContext.mainViewUser, mediaStream, currentLayout, requestUpdate]);

  // Bundle all props needed by ParticipantCard so we don't re-create objects inline
  const cardProps = {
    avatarActionState,
    activeVideo,
    networkQuality,
    aspectRatio,
    optionsOfVideoResolution,
    onVideoResolutionChange,
    setVideoPlayerRef
  };

  return (
    <div className="video-page">
      {/* <Header userName={userName} email="" /> */}

      <div
        ref={videoAreaRef}
        className="viewport"
        style={{
          flex: 1,
          minHeight: MIN_CONTAINER_H + 32,
          display: 'flex',
          flexDirection: 'column',
          padding: '16px',
          overflow: 'hidden'
        }}
      >
        {/* Flex row containing ShareView + SidePanel (or other layout) */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 10 }}>
          {/* ShareView is always mounted here, hides/shows via its CSS class */}
          {viewContext.speakerView && viewContext.pinViewUser == null && (
            <ShareView key="share-view" ref={shareViewRef} onReceiveSharingChange={setIsReceiveSharing} />
          )}

          {/* Remaining part: SidePanel when sharing, or normal layout */}
          {isReceiveSharing && viewContext.pinViewUser == null && !viewContext.galleryView ? (
            <SidePanel sideParticipants={visibleParticipants} containerHeight={containerHeight} cardProps={cardProps} />
          ) : viewContext.speakerView && viewContext.mainViewUser ? (
            <SidebarLayout
              isSharing={isReceiveSharing}
              mainParticipant={viewContext.mainViewUser}
              sideParticipants={visibleParticipants}
              containerHeight={containerHeight}
              cardProps={cardProps}
              targetRatio={targetRatio}
              shareViewNode={
                !viewContext.speakerView || viewContext.pinViewUser != null ? (
                  <ShareView key="share-view-1" ref={shareViewRef} onReceiveSharingChange={setIsReceiveSharing} />
                ) : undefined
              }
            />
          ) : (
            <GridLayout
              isSharing={isReceiveSharing}
              participants={visibleParticipants}
              cardProps={cardProps}
              shareViewNode={
                !viewContext.speakerView || viewContext.pinViewUser != null ? (
                  <ShareView key="share-view-2" ref={shareViewRef} onReceiveSharingChange={setIsReceiveSharing} />
                ) : undefined
              }
            />
          )}
        </div>

        {totalPage > 1 && (
          <Pagination page={page} totalPage={totalPage} setPage={setPage} inSharing={isReceiveSharing} />
        )}
      </div>

      <VideoFooter
        className="video-operations"
        sharing
        selfShareCanvas={shareViewRef.current?.selfShareRef}
        slotBookingId={slotBookingId}
        userId={userId}
        initialIsStartedAudio={routeState?.isStartedAudio}
        initialIsStartedVideo={routeState?.isStartedVideo}
        initialIsMuted={routeState?.isMuted}
        initialHasMicrophonePermission={routeState?.hasMicrophonePermission}
        initialHasCameraPermission={routeState?.hasCameraPermission}
        initialActiveMicrophone={routeState?.activeMicrophone}
        initialActiveCamera={routeState?.activeCamera}
        initialActiveSpeaker={routeState?.activeSpeaker}
        onMobileChangeRatio={(userId, landscape) => {
          setMobileOrientations((prev) => new Map(prev).set(String(userId), landscape));
        }}
      />
    </div>
  );
};

export default VideoContainer;
